import { Link } from 'react-router-dom';
import { useSEO, SITE_URL } from '@/lib/seo';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter, CONTACT_EMAIL } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';

const Section = ({ n, title, children }) => (
  <section className="mb-9">
    <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-3">
      {n}. {title}
    </h2>
    <div className="font-['Outfit'] text-[#1C1917]/80 leading-relaxed space-y-3 text-[15px]">
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => {
  useSEO({
    title: 'Privacy Policy | Masala Spin',
    description:
      'How Masala Spin handles your information: recipe and contact submissions, browser storage, analytics, cookies and Google AdSense advertising.',
    canonical: `${SITE_URL}/privacy-policy`,
  });

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-14">
        <Breadcrumbs items={[{ name: 'Privacy Policy' }]} />

        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-3">
          Privacy Policy
        </h1>
        <p className="font-['Outfit'] text-sm text-[#1C1917]/55 mb-10">Last updated: 14 June 2026</p>

        <div className="font-['Outfit'] text-[#1C1917]/80 leading-relaxed mb-10 text-[15px]">
          <p>
            This policy explains what information Masala Spin (masalaspin.com) collects, why, and
            who else is involved. Masala Spin is an independently run recipe website. You do not need
            an account to use it, and we do not ask for personal details unless you choose to send us
            something.
          </p>
        </div>

        <Section n="1" title="Information you give us">
          <p>We only receive personal information when you send it:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Recipe submissions and contact messages.</strong> If you use the submission
              form, the <Link to="/contact" className="text-[#F97316] hover:underline">contact form</Link>,
              or email us, we receive the name, email address, subject and message you provide, plus
              the date and time. We use these only to read and reply to your message and, for recipe
              submissions, to consider publishing the recipe.
            </li>
            <li>
              <strong>Emailing yourself a meal plan.</strong> If you ask us to email your weekly meal
              plan, the email address you type and the plan itself are sent to our server so the mail
              can be delivered.
            </li>
            <li>
              <strong>Chef bot input.</strong> The ingredients you type into the AI chef bot are sent
              to our server and on to a third-party AI provider (see section 4) to generate
              suggestions.
            </li>
          </ul>
        </Section>

        <Section n="2" title="Information stored in your own browser">
          <p>
            Several features use your browser's <strong>localStorage</strong>. This data stays on
            your device and is not sent to us unless a feature explicitly does so:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>your weekly meal plan;</li>
            <li>the email address you last used for a meal-plan email, so you do not have to retype it;</li>
            <li>dishes you save from the "decide my meal" section;</li>
            <li>your vegetarian / non-vegetarian preference for the 21-day plan;</li>
            <li>a randomly generated ID used by the chef bot to keep one conversation together;</li>
            <li>
              a randomly generated ID used for recipe ratings, so the same browser cannot rate a
              recipe twice. This ID is not linked to your name or email.
            </li>
          </ul>
          <p>
            Clearing your browser data deletes all of the above, including your saved meal plan.
          </p>
        </Section>

        <Section n="3" title="Information collected automatically">
          <p>
            Like any website, our hosting and content-delivery infrastructure processes technical
            information needed to deliver pages, including your IP address, browser type, device type
            and the pages you request.
          </p>
          <p>We also use two analytics services:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Google Analytics 4</strong> — measures pageviews, referrers and general usage
              patterns. Google may set cookies for this purpose.
            </li>
            <li>
              <strong>Cloudflare Web Analytics</strong> — aggregate traffic measurement that does not
              use cookies for tracking.
            </li>
          </ul>
          <p>
            Recipe ratings you submit are stored on our server against the recipe and the random
            browser ID described above.
          </p>
        </Section>

        <Section n="4" title="Third parties we share information with">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Google AdSense</strong> — serves the advertising on this site (see section 5).
            </li>
            <li>
              <strong>Google Analytics</strong> and <strong>Cloudflare</strong> — analytics and
              content delivery.
            </li>
            <li>
              <strong>Resend</strong> — the email service used to deliver contact and recipe
              submissions to our inbox, and to send meal-plan emails you request.
            </li>
            <li>
              <strong>An AI language model provider (Google Gemini, accessed through an API
              gateway)</strong> — receives the ingredient text you type into the chef bot in order to
              generate suggestions.
            </li>
            <li>
              <strong>Our database and hosting providers</strong> — store the site's data on our
              behalf.
            </li>
          </ul>
          <p>
            We do not sell your personal information, and we do not share submissions with anyone
            beyond the providers needed to operate the site or where the law requires it.
          </p>
        </Section>

        <Section n="5" title="Advertising and Google AdSense">
          <p>
            Masala Spin is funded by advertising. We use <strong>Google AdSense</strong> to display
            ads.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Third-party vendors, including Google, use cookies to serve ads based on your prior
              visits to this website or other websites.
            </li>
            <li>
              Google's use of advertising cookies enables it and its partners to serve ads to you
              based on your visit to this site and/or other sites on the internet.
            </li>
            <li>
              You can opt out of personalised advertising by visiting{' '}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline">
                Google's Ads Settings
              </a>, and you can opt out of some third-party vendors' cookies at{' '}
              <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline">
                aboutads.info/choices
              </a>.
            </li>
            <li>
              More information is available in{' '}
              <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline">
                Google's policy on how it uses data from sites that use its services
              </a>.
            </li>
          </ul>
        </Section>

        <Section n="6" title="Cookies and similar technologies">
          <p>
            We do not set cookies ourselves for advertising or profiling. Cookies on this site come
            from the third-party services above — primarily Google (advertising and analytics). The
            site's own features use localStorage rather than cookies, as described in section 2.
          </p>
          <p>
            You can block or delete cookies in your browser settings. Some third-party features may
            stop working if you do, but the recipes and the wheel will still work.
          </p>
        </Section>

        <Section n="7" title="How long we keep information">
          <p>
            Recipe submissions, contact messages and ratings are kept in our database for as long as
            they remain useful for running the site, unless you ask us to delete them. Emails you
            send us remain in our inbox. Analytics data is retained according to the retention
            settings of the services above.
          </p>
          <p>
            We limit access to submissions to the people who run the site. We cannot guarantee that
            any transmission over the internet or method of storage is completely secure, so please
            do not send us sensitive personal information.
          </p>
        </Section>

        <Section n="8" title="Your rights">
          <p>
            Depending on where you live, you may have the right to ask for a copy of the personal
            information we hold about you, to have it corrected or deleted, to object to processing,
            or to withdraw consent. Because we hold very little — essentially what you have sent us —
            these requests are usually simple to handle.
          </p>
          <p>
            Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#F97316] hover:underline break-all">
              {CONTACT_EMAIL}
            </a>{' '}
            from the address you contacted us with and tell us what you would like us to do.
          </p>
        </Section>

        <Section n="9" title="Children">
          <p>
            The site is intended for a general audience and is not directed at children under 13. We
            do not knowingly collect personal information from children. If you believe a child has
            sent us personal information, contact us and we will delete it.
          </p>
        </Section>

        <Section n="10" title="Payments">
          <p>
            We do not currently take payments on this site. The 21-day meal plan and every recipe are
            free to use.
          </p>
        </Section>

        <Section n="11" title="Changes to this policy">
          <p>
            We will update this page when the site changes in a way that affects your information,
            and we will update the "last updated" date above.
          </p>
        </Section>

        <Section n="12" title="Contact us">
          <p>
            For anything relating to privacy, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#F97316] hover:underline break-all">
              {CONTACT_EMAIL}
            </a>{' '}
            or use the{' '}
            <Link to="/contact" className="text-[#F97316] hover:underline">contact page</Link>. You
            may also want to read our{' '}
            <Link to="/terms" className="text-[#F97316] hover:underline">Terms of Use</Link>.
          </p>
        </Section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default PrivacyPolicy;
